<template>
    <v-data-table :headers="headers" :items="serverList" item-key="name" class="border h-[500px] bg-white text-black min-h-[500px]"  items-per-page="-1"
    style="font-size: 13px;" :style="`height: ${computedHeight}px;`">
        <template #[`item.no`]="{ index }">
            {{ index + 1 }}
        </template>

        <template #[`item.modemName`]="{ item }">
            <div class="flex items-center">
                <v-icon icon="mdi-circle" size="15" :color="item.conn_status ? 'green' : 'red'" class="mr-2"></v-icon>
                {{ item.modemName }}
                <!-- outage reported by ProxyMonitor (open, or ended within the hour) -->
                <span v-if="item.outage" class="outage-pill" :class="item.outage.state === 'outage' ? 'is-out' : 'is-ok'"
                    :title="item.active_licenses + ' active licence' + (item.active_licenses === 1 ? '' : 's') + ' on this server'">
                    {{ item.outage.state === 'outage' ? 'Outage · ' + item.outage.minutes + ' min · ' + item.active_licenses + ' active' : 'Back online ' + new Date(item.outage.ended_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}
                </span>
            </div>
        </template>
        <template #[`item.location`]="{ item }">
            {{ item.country + " " + item.city }}
        </template>
        <!-- spare capacity, refreshed every 3 hours: ProxySmart = modems with no port, X-Proxy = plugged-in positions with no selling port -->
        <template #[`item.unused`]="{ item }">
            <span v-if="!item.unused_info || item.unused_info.count === null || item.unused_info.count === undefined" class="unused unused--none"
                :title="item.unused_info && item.unused_info.ok === false ? 'No answer at the last check' : 'Not checked yet'">–</span>
            <!-- just the number; hovering it lists the modems / positions -->
            <v-tooltip v-else location="top" max-width="340" open-delay="100">
                <template #activator="{ props }">
                    <span v-bind="props" class="unused" :class="{ 'unused--zero': item.unused_info.count === 0, 'unused--stale': item.unused_info.ok === false }" tabindex="0">{{ item.unused_info.count }}</span>
                </template>
                <div class="unused-tip">
                    <div class="unused-tip__title">{{ unusedTitle(item.unused_info) }}</div>
                    <div v-if="item.unused_info.items && item.unused_info.items.length" class="unused-tip__items">{{ item.unused_info.items.join(', ') }}</div>
                    <div v-if="item.unused_info.ok === false" class="unused-tip__warn">No answer at the last check; these figures are from {{ ago(item.unused_info.checked_at) }} ago.</div>
                </div>
            </v-tooltip>
        </template>
        <template v-slot:[`item.buttonBar`]="{ item }">
            <v-menu offset-y location="bottom">
                <template v-slot:activator="{ props, on, isActive }">
                    <v-btn v-bind="props" v-on="{ ...on }"  density="compact"
                        :style="isActive ? 'border-bottom-left-radius: 0px;border-bottom-right-radius: 0px;' : ''"
                        style="outline: none; font-size: 10px; " class="w-32 z-10 bg-dark text-light ">
                        Actions
                        <v-icon icon="mdi-chevron-down" size="14"></v-icon>
                    </v-btn>
                </template>
                <v-list class="w-32 flex-1 flex flex-col px-0 py-0  z-0"
                    style="border-top-left-radius: 0px;border-top-right-radius: 0px">
                    <v-btn v-for="(button, index) in actionButtons" :key="index" @click="clickAction(item, button)"
                        rounded="0" class="text-center bg-dark text-light"
                        style="font-size: 10px; width: 128px; outline: none; border-bottom: 1px solid #345;"
                        :data-bs-toggle="button === 'Update' && 'modal'"
                        :data-bs-target="button === 'Update' && '#serverUpdateModal'">
                        {{ button}}
                    </v-btn>
                </v-list>
            </v-menu>
        </template>
    </v-data-table>
    
    <div>
        <UpdateInfoDialog :item="selectedItem" @close="updateInfoDialogFlag=false" />
    </div>
</template>
<script setup>
import { ref, defineProps, watchEffect,  onMounted, onBeforeUnmount } from "vue"
import { useAdminServerStore, useAlertStore } from "@/stores"
import UpdateInfoDialog from "./Modals/UpdateInfoDialog.vue"


const adminServerStore = useAdminServerStore()
const ago = (value) => { const mins = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60000)); return !value ? 'an earlier check' : mins < 90 ? `${mins} min` : `${Math.round(mins / 60)} hours`; };
const unusedTitle = (info) => info.count === 0 ? `All ${info.total} ${info.kind === 'modems' ? 'modems have a port' : 'plugged-in positions have a selling port'}` : `${info.count} of ${info.total} ${info.kind === 'modems' ? (info.count === 1 ? 'modem has no port' : 'modems have no port') : (info.count === 1 ? 'plugged-in position has no selling port' : 'plugged-in positions have no selling port')}`;
const headers = ref(
    [
        { title: 'No', align: 'center', key: 'no' },
        { title: 'Modem Name', align: 'center', key: 'modemName' },
        { title: 'Domain', align: 'center', key: 'domain' },
        { title: 'Location', align: 'center', key: 'location' },
        { title: 'Status', align: 'center', key: 'status' },
        { title: 'ISP', align: 'center', key: 'isp' },
        { title: 'Server Type', align: 'center', key: 'modem_type' },
        { title: 'Unused', align: 'center', key: 'unused', value: (item) => item?.unused_info?.count ?? -1 },   // sorts by the number
        { title: 'Actions', align: 'center', key: 'buttonBar' },
    ]
);
const props = defineProps({
    serverList: {
        type: Object, // Assuming user object has a name property
        required: true
    },
});

const offsetHeight = 400; // Adjust this value as needed

const computedHeight = ref(window.innerHeight - offsetHeight);

const handleResize = () => {
  computedHeight.value = window.innerHeight - offsetHeight;
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});

const serverList = ref(props.serverList)
watchEffect(() => {
    serverList.value = props.serverList
}, [props.serverList])

const actionButtons = ref(
    ["Update", "Synchronize", "Pause/Resume", "Change history", "Delete"]
)

const openUpdateInfoDialog = (item) => {
    selectedItem.value = item
    updateInfoDialogFlag.value = true
}
const clickAction = (item, button) => {
    switch (button) {
        case "Update":
            openUpdateInfoDialog(item)
            break
        case "Synchronize":
            adminServerStore.syncServer(item.uuid)
            break
        case "Pause/Resume":
            adminServerStore.pauseOrResume(item.uuid)
            break
        case "Change history":   // Audit log for this server, in a new tab
            window.open(`/admin/audit?entity_type=server&entity_id=${encodeURIComponent(item.uuid)}`, "_blank", "noopener")
            break
        case "Delete":
            if(item.num !== 0) {
                const alertStore = useAlertStore()
                alertStore.warning("Can't delete used server")
                return
            }
            adminServerStore.deleteServer(item.uuid)

    }
}

const updateInfoDialogFlag = ref(false)
const selectedItem = ref(null)

</script>

<style scoped>
.unused { display: inline-block; min-width: 28px; padding: 1px 8px; border-radius: 999px; font-size: 15px; font-weight: 700; cursor: default; }
.unused:not(.unused--zero):not(.unused--none) { background: var(--ui-pri-soft); cursor: help; }
.unused--zero, .unused--none { color: var(--ui-dim); font-weight: 500; }
.unused--stale { outline: 1px dashed var(--ui-warn); }
.unused-tip { font-size: 12.5px; line-height: 1.45; }
.unused-tip__title { font-weight: 600; }
.unused-tip__items { margin-top: 3px; word-break: break-word; }
.unused-tip__warn { margin-top: 4px; opacity: 0.85; }
</style>

<style scoped>
.outage-pill { margin-left: 8px; padding: 1px 8px; border-radius: 999px; font-size: 11.5px; font-weight: 700; white-space: nowrap; }
.outage-pill.is-out { background: #4a2a2f; color: #f6c9c9; }
.outage-pill.is-ok { background: #21403d; color: #9fe1cb; }
</style>
